<template>
  <b-row class="settings-panels">
    <b-col cols="12" class="code-panel">
      <div class="code-card">
        <header class="code-head">
          <div class="code-mark" aria-hidden="true">
            <font-awesome-icon icon="code" />
          </div>
          <div class="code-head-copy">
            <div class="code-title">Custom code</div>
            <p class="code-lead">
              JavaScript files run in this order when the live app loads. A problem in one file is skipped, and the app still opens.
            </p>
          </div>
          <span class="code-pill" :class="files.length ? 'code-pill-ready' : 'code-pill-empty'">
            {{ files.length ? fileCountLabel : 'not added' }}
          </span>
        </header>

        <div v-if="!files.length" class="code-empty">
          <p>Add a file for each script the live app should run.</p>
          <button type="button" class="code-add" @click="addFile">add file</button>
        </div>

        <div v-else class="code-body">
          <div class="code-files" role="listbox" aria-label="Custom code files">
            <button
              v-for="(file, index) in files"
              :key="file.uniqueTitle"
              type="button"
              class="code-file"
              :class="{ 'is-on': file.uniqueTitle === activeId }"
              role="option"
              :aria-selected="file.uniqueTitle === activeId ? 'true' : 'false'"
              @click="activeId = file.uniqueTitle"
            >
              <span class="code-file-index">{{ index + 1 }}</span>
              <span class="code-file-name">{{ file.name }}</span>
            </button>
            <button type="button" class="code-add code-add-inline" @click="addFile">add file</button>
          </div>

          <div v-if="active" class="code-editor">
            <div class="code-editor-bar">
              <label class="code-name">
                <span class="code-name-label">File</span>
                <b-form-input v-model="active.name" aria-label="File name" autocomplete="off" spellcheck="false" />
              </label>
              <button type="button" class="code-tool" :disabled="activeIndex === 0" @click="moveActive(-1)">move up</button>
              <button type="button" class="code-tool" :disabled="activeIndex === files.length - 1" @click="moveActive(1)">move down</button>
              <button type="button" class="code-tool" @click="removeActive">remove</button>
            </div>
            <JsCodeEditor
              ref="editor"
              v-model="active.code"
              :aria-label="`JavaScript for ${active.name}`"
              @save="save"
            />
          </div>
        </div>

        <footer class="code-actions">
          <p v-if="error" class="code-error">{{ error }}</p>
          <span v-else class="code-note">A later file can use a function from an earlier one. Save before you leave this screen.</span>
          <button type="button" class="code-save" :disabled="saving || !files.length && !hadFiles" @click="save">
            <template v-if="saving">saving...</template>
            <template v-else-if="saved">saved!</template>
            <template v-else>save custom code</template>
          </button>
        </footer>
      </div>
    </b-col>
  </b-row>
</template>

<script>

import axios from 'axios'
import JsCodeEditor from '../JsCodeEditor.vue'
import { onDesignerChange, raiseDesignerWarning } from '../../lib/designer-sync.js'
import { blankCustomCodeFile, customCodeForRead, normalizeCustomCode } from '../../../../shared/custom-code.js'

function snapshot (files) {

  return JSON.stringify((files || []).map((file) => ({
    uniqueTitle: file.uniqueTitle,
    name: file.name,
    code: file.code
  })))
}

export default {
  name: 'SettingsCustomCode',
  components: { JsCodeEditor },
  props: {
    application: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      files: [],
      activeId: '',
      savedSnapshot: snapshot([]),
      hadFiles: false,
      saving: false,
      saved: false,
      error: ''
    }
  },
  computed: {
    activeIndex () {

      return this.files.findIndex((file) => file.uniqueTitle === this.activeId)
    },
    active () {

      return this.activeIndex >= 0 ? this.files[this.activeIndex] : null
    },
    fileCountLabel () {

      return this.files.length === 1 ? '1 file' : `${this.files.length} files`
    },
    dirty () {

      return snapshot(this.files) !== this.savedSnapshot
    }
  },
  async mounted () {

    await this.load()
    this._stopDesignerSync = onDesignerChange((change) => this.applyDesignerChange(change))
  },
  beforeUnmount () {

    if (this._stopDesignerSync) this._stopDesignerSync()
  },
  watch: {
    async application () {

      await this.load()
    },
    files: {
      deep: true,
      handler () {

        if (this.saving) return
        if (snapshot(this.files) !== this.savedSnapshot) this.saved = false
      }
    }
  },
  methods: {
    applyFiles (customCode) {

      const next = customCodeForRead(customCode).files.map((file) => ({ ...file }))

      this.files = next
      this.hadFiles = next.length > 0
      this.savedSnapshot = snapshot(next)

      if (!next.some((file) => file.uniqueTitle === this.activeId)) {

        this.activeId = next.length ? next[0].uniqueTitle : ''
      }
    },
    async load () {

      try {

        const res = await axios.get(`${VUE_APP_PROXY_SERVER_URL}/settings`, {
          headers: { 'application-unique-title': this.application }
        })

        this.applyFiles(res.data && res.data.customCode)
        this.error = ''
      } catch (err) {

        console.log('failed to load custom code', err)
      }
    },
    async applyDesignerChange (change) {

      const keys = change && change.keys || []
      const relevant = keys.includes('settings:code') || keys.includes('settings') || keys.includes('application')

      if (!relevant) return

      if (this.dirty) {

        raiseDesignerWarning(change, 'custom code')

        return
      }

      await this.load()
    },
    addFile () {

      const file = blankCustomCodeFile(this.files)

      this.files.push(file)
      this.activeId = file.uniqueTitle
      this.saved = false
      this.error = ''
      this.$nextTick(() => {

        if (this.$refs.editor) this.$refs.editor.focus()
      })
    },
    moveActive (delta) {

      const index = this.activeIndex
      const next = index + delta

      if (index < 0 || next < 0 || next >= this.files.length) return

      const files = this.files.slice()
      const [file] = files.splice(index, 1)

      files.splice(next, 0, file)
      this.files = files
      this.saved = false
    },
    removeActive () {

      const file = this.active

      if (!file) return

      if (file.code.trim() && !confirm(`Remove ${file.name}?`)) return

      const index = this.activeIndex

      this.files.splice(index, 1)
      const neighbor = this.files[Math.min(index, this.files.length - 1)]

      this.activeId = neighbor ? neighbor.uniqueTitle : ''
      this.saved = false
    },
    async save () {

      const normalized = normalizeCustomCode({ files: this.files })

      if (normalized.error) {

        this.error = normalized.error
        this.saved = false

        return
      }

      this.saving = true
      this.error = ''
      this.saved = false

      try {

        const res = await axios.put(
          `${VUE_APP_PROXY_SERVER_URL}/settings`,
          { customCode: normalized.customCode },
          { headers: { 'application-unique-title': this.application } }
        )

        this.applyFiles(res.data && res.data.customCode)
        this.saved = true
      } catch (err) {

        const data = err.response && err.response.data

        this.error = (typeof data === 'string' && data) || 'Could not save custom code.'
      } finally {

        this.saving = false
      }
    }
  }
}

</script>

<style scoped>

.code-panel {
  padding: 1.25rem 1.25rem 1.5rem;
  max-width: 72rem;
  height: calc(100vh - 102px);
  display: flex;
  flex-direction: column;
}

.code-card {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: #141412;
  border: 1px solid #2a2a27;
  border-radius: 12px;
  overflow: hidden;
}

.code-head {
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
  padding: 1.05rem 1.2rem 1rem;
  border-bottom: 1px solid #2a2a27;
  background: linear-gradient(180deg, #1a1a17 0%, #141412 100%);
}

.code-mark {
  flex-shrink: 0;
  width: 2.1rem;
  height: 2.1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: rgba(154, 209, 43, 0.12);
  color: #c6ee7a;
}

.code-head-copy {
  flex: 1;
  min-width: 0;
}

.code-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #e8e8e6;
  margin-bottom: 2px;
}

.code-lead,
.code-note {
  margin: 0;
  font-size: 0.75rem;
  color: #707070;
  line-height: 1.4;
}

.code-pill {
  flex-shrink: 0;
  margin-top: 0.15rem;
  font-size: 0.68rem;
  font-weight: 600;
  padding: 0.22rem 0.55rem;
  border-radius: 999px;
  border: 1px solid #2a2a27;
}

.code-pill-ready {
  color: #c6ee7a;
  background: rgba(154, 209, 43, 0.12);
  border-color: rgba(154, 209, 43, 0.4);
}

.code-pill-empty {
  color: #8a8a88;
  background: #1e1e1b;
}

.code-empty {
  padding: 1.4rem 1.2rem 1.6rem;
  color: #b0b0ae;
  font-size: 0.85rem;
}

.code-body {
  flex: 1;
  min-height: 0;
  display: flex;
}

.code-files {
  flex: 0 0 14rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.75rem;
  border-right: 1px solid #2a2a27;
  overflow: auto;
  background: #121210;
}

.code-file {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  width: 100%;
  text-align: left;
  border: 1px solid transparent;
  background: transparent;
  color: #c0c0be;
  border-radius: 6px;
  padding: 0.4rem 0.5rem;
  cursor: pointer;
  font-size: 0.78rem;
}

.code-file:hover {
  background: #1e1e1b;
}

.code-file.is-on {
  border-color: rgba(154, 209, 43, 0.55);
  background: rgba(154, 209, 43, 0.08);
  color: #e8e8e6;
}

.code-file-index {
  flex: 0 0 1.1rem;
  color: #707070;
  font-size: 0.68rem;
}

.code-file-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.code-editor {
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  padding: 0.75rem;
}

.code-editor-bar {
  display: flex;
  align-items: flex-end;
  gap: 0.45rem;
}

.code-name {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin: 0;
}

.code-name-label {
  font-size: 0.72rem;
  color: #c0c0be;
}

.code-tool,
.code-add {
  border: 1px dashed #3a3a37;
  background: transparent;
  color: #b0b0ae;
  border-radius: 6px;
  font-size: 0.75rem;
  padding: 0.38rem 0.65rem;
  cursor: pointer;
}

.code-tool {
  border-style: solid;
  margin-bottom: 0.05rem;
}

.code-tool:hover,
.code-add:hover {
  border-color: #9ad12b;
  color: #e8e8e6;
}

.code-tool:disabled {
  opacity: 0.4;
  cursor: default;
}

.code-add-inline {
  margin-top: 0.35rem;
}

.code-editor :deep(.js-code) {
  flex: 1;
  min-height: 12rem;
}

.code-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.85rem 1.2rem;
  background: #10100e;
  border-top: 1px solid #2a2a27;
}

.code-note,
.code-error {
  flex: 1;
  min-width: 0;
}

.code-error {
  margin: 0;
  color: #f0a0a0;
  font-size: 0.8rem;
}

.code-save {
  flex: 0 0 auto;
  margin-left: auto;
  min-width: 10rem;
  padding: 0.5rem 1.15rem;
  border: none;
  border-radius: 6px;
  background-color: #9ad12b;
  color: #10140a;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
}

.code-save:hover {
  background-color: #b0e840;
}

.code-save:disabled {
  opacity: 0.55;
  cursor: default;
}

@media (max-width: 800px) {
  .code-panel {
    height: auto;
  }

  .code-body {
    flex-direction: column;
  }

  .code-files {
    flex: none;
    border-right: 0;
    border-bottom: 1px solid #2a2a27;
    max-height: 10rem;
  }

  .code-editor {
    min-height: 22rem;
  }

  .code-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .code-save {
    width: 100%;
  }
}

</style>
